/**
 * Camp Deduplication Service
 * 
 * Handles deduplication of email scout camps with My Programs camps
 * Uses name + location matching strategy
 */

/**
 * Normalize camp name for matching
 */
export const normalizeCampName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' '); // Normalize whitespace
};

/**
 * Normalize location for matching
 */
export const normalizeLocation = (location) => {
  if (!location) return '';
  const normalized = location
    .toLowerCase()
    .trim()
    // Remove zip codes
    .replace(/\d{5}(-\d{4})?/g, '')
    // Remove common address parts that vary
    .replace(/\b(street|st|avenue|ave|road|rd|drive|dr|lane|ln|blvd|boulevard|way|circle|ct|court)\b/g, '')
    // Remove suite/unit numbers
    .replace(/suite\s+\w+|unit\s+\w+/gi, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
  
  return normalized;
};

/**
 * Extract city and state from location string
 */
const extractCityState = (location) => {
  if (!location) return { city: '', state: '' };
  
  // Try to match common US city, state patterns
  // e.g., "Atlanta, GA", "North Canton, OH 44720", "New York, NY 10001"
  const cityStateMatch = location.match(/([^,]+),\s*([A-Z]{2})(?:\s+\d{5})?/i);
  if (cityStateMatch) {
    return {
      city: normalizeCampName(cityStateMatch[1]),
      state: cityStateMatch[2].toUpperCase()
    };
  }
  
  // Fallback: split by comma and take last two parts
  const parts = location.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const statePart = parts[parts.length - 1].replace(/\d{5}.*$/, '').trim();
    const cityPart = parts[parts.length - 2];
    return {
      city: normalizeCampName(cityPart),
      state: statePart.toUpperCase().substring(0, 2)
    };
  }
  
  return {
    city: normalizeLocation(location),
    state: ''
  };
};

/**
 * Check if two camps match by name + location
 * @param {Object} camp1 - First camp (from email scout)
 * @param {Object} camp2 - Second camp (from My Programs)
 * @returns {boolean} True if camps match
 */
export const campsMatch = (camp1, camp2) => {
  // Get names and locations
  const name1 = normalizeCampName(camp1.camp_name || camp1.name || camp1.title || '');
  const name2 = normalizeCampName(camp2.program_data?.name || camp2.name || camp2.title || '');
  
  const location1 = normalizeLocation(
    camp1.camp_address || 
    camp1.camp_location || 
    camp1.location || 
    camp1.formatted_address || 
    ''
  );
  
  const location2 = normalizeLocation(
    camp2.program_data?.location || 
    camp2.program_data?.general_location || 
    camp2.program_data?.formatted_address ||
    camp2.location ||
    camp2.formatted_address ||
    ''
  );

  // Must have names to match
  if (!name1 || !name2) {
    return false;
  }

  // Exact name match
  if (name1 === name2) {
    // If locations provided, check if they match
    if (location1 && location2) {
      const cityState1 = extractCityState(location1);
      const cityState2 = extractCityState(location2);
      
      // Match if city matches and state matches (if available)
      if (cityState1.city && cityState2.city) {
        if (cityState1.city === cityState2.city) {
          // Both have states - must match
          if (cityState1.state && cityState2.state) {
            return cityState1.state === cityState2.state;
          }
          // At least one doesn't have state - city match is enough
          return true;
        }
      }
      
      // Fallback: check if locations share significant words
      const locationWords1 = location1.split(/\s+/).filter(w => w.length > 3);
      const locationWords2 = location2.split(/\s+/).filter(w => w.length > 3);
      const matchingWords = locationWords1.filter(w => locationWords2.includes(w));
      
      // If at least 2 location words match, consider it a match
      return matchingWords.length >= 2;
    }
    
    // No location data, exact name match is enough
    return true;
  }

  // Fuzzy name match: check if one name contains the other (for abbreviations)
  if (name1.includes(name2) || name2.includes(name1)) {
    // If locations match, it's likely the same camp
    if (location1 && location2 && location1 === location2) {
      return true;
    }
  }

  return false;
};

/**
 * Deduplicate email scout camps with My Programs camps
 * @param {Array} emailScoutCamps - Camps from email scout (user_camp_emails)
 * @param {Array} myPrograms - User programs from user_programs table
 * @returns {Array} Deduplicated list with metadata
 */
export const deduplicateEmailScoutWithMyPrograms = (emailScoutCamps = [], myPrograms = []) => {
  const deduplicated = [];
  const matchedProgramIds = new Set();

  // Process email scout camps
  emailScoutCamps.forEach(emailScoutCamp => {
    // Find matching program in My Programs
    const matchingProgram = myPrograms.find(program => 
      campsMatch(emailScoutCamp, program)
    );

    if (matchingProgram) {
      // Duplicate found - mark both
      matchedProgramIds.add(matchingProgram.id);
      
      // Add email scout camp with metadata
      deduplicated.push({
        ...emailScoutCamp,
        _isDuplicate: true,
        _matchedProgramId: matchingProgram.id,
        _source: 'both',
        _matchedProgram: matchingProgram
      });
    } else {
      // No match - unique email scout camp
      deduplicated.push({
        ...emailScoutCamp,
        _isDuplicate: false,
        _matchedProgramId: null,
        _source: 'email-scout'
      });
    }
  });

  // Add My Programs camps that don't have email scout matches
  myPrograms.forEach(program => {
    if (!matchedProgramIds.has(program.id)) {
      deduplicated.push({
        ...program,
        _isDuplicate: false,
        _matchedProgramId: null,
        _source: 'my-programs'
      });
    }
  });

  return deduplicated;
};

/**
 * Get deduplication statistics
 */
export const getDeduplicationStats = (deduplicatedCamps) => {
  const stats = {
    total: deduplicatedCamps.length,
    emailScout: 0,
    myPrograms: 0,
    duplicates: 0
  };

  deduplicatedCamps.forEach(camp => {
    if (camp._source === 'email-scout') {
      stats.emailScout++;
    } else if (camp._source === 'my-programs') {
      stats.myPrograms++;
    } else if (camp._source === 'both') {
      stats.duplicates++;
      stats.emailScout++;
      stats.myPrograms++;
    }
  });

  return stats;
};

